import { Button } from "@d12labs/ui/button";
import { ListHeader, ListRow } from "@d12labs/ui/data-list";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { cn } from "@d12labs/utils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Calendar, ChevronRight, Download, Plus, Presentation, Sparkles } from "lucide-react";
import { meetingPerfApi } from "../api";
import { Avatar } from "../components/avatar";
import { Delta } from "../components/delta";
import { ScoreRing } from "../components/score-badge";
import { ScoreBar, scoreToneClass } from "../components/score-bar";
import { Sparkline } from "../components/sparkline";
import { computeStreaks } from "../streaks";
import {
	CATEGORY_KEYS,
	CATEGORY_LABELS,
	overallScoreColor,
	type SpeakerWithSeries,
} from "../types";

// ── Pure helpers ────────────────────────────────────────────────────────────

/**
 * Derive a per-date avg-overall sparkline from all tracked speakers' series.
 * Groups by date (YYYY-MM-DD), averages overall scores. Returns values oldest→newest.
 */
function deriveAvgScoreSparkline(trackedSpeakers: SpeakerWithSeries[]): number[] {
	const byDate = new Map<string, number[]>();
	for (const { series } of trackedSpeakers) {
		for (const pt of series) {
			const day = pt.analyzedAt.slice(0, 10);
			const arr = byDate.get(day) ?? [];
			arr.push(pt.overallScore);
			byDate.set(day, arr);
		}
	}
	const sorted = [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b));
	return sorted.map(([, scores]) => {
		const sum = scores.reduce((acc, s) => acc + s, 0);
		return sum / scores.length;
	});
}

/**
 * Returns the 3 lowest-scoring dimensions averaged across all tracked speakers'
 * latest analysis, sorted ascending (worst first).
 */
function computeTeamFocusAreas(
	trackedSpeakers: SpeakerWithSeries[],
): { label: string; avg: number }[] {
	if (trackedSpeakers.length === 0) return [];
	const totals: Record<string, { sum: number; count: number }> = {};
	for (const { series } of trackedSpeakers) {
		const latest = series[series.length - 1];
		if (!latest) continue;
		for (const key of CATEGORY_KEYS) {
			const score = latest.categories[key] ?? 0;
			const entry = totals[key] ?? { sum: 0, count: 0 };
			entry.sum += score;
			entry.count += 1;
			totals[key] = entry;
		}
	}
	return CATEGORY_KEYS.map((key) => {
		const entry = totals[key] ?? { sum: 0, count: 0 };
		const avg = entry.count > 0 ? entry.sum / entry.count : 0;
		return { label: CATEGORY_LABELS[key], avg };
	})
		.sort((a, b) => a.avg - b.avg)
		.slice(0, 3);
}

// ── Sub-components ───────────────────────────────────────────────────────────

/** 2px primary top-of-page loading bar. */
function LoadingBar() {
	return <div className="h-0.5 w-full bg-primary" />;
}

/**
 * Hero "Your performance" section — flat card with three hairline-separated zones.
 * No nested surface-2 boxes (the dimension row has no boxes).
 */
function HeroSection({ sw }: { sw: SpeakerWithSeries }) {
	const { speaker, series } = sw;
	const latest = series[series.length - 1];
	const earliest = series[0];
	if (!latest || !earliest) return null;

	const delta = latest.overallScore - earliest.overallScore;
	const streaks = computeStreaks(series);
	const sparkValues = series.map((p) => p.overallScore);

	// Top-lifted category for AI insight
	const lifts = CATEGORY_KEYS.map((k) => ({
		k,
		lift: (latest.categories[k] ?? 0) - (earliest.categories[k] ?? 0),
	})).sort((a, b) => b.lift - a.lift);
	const topLift = lifts[0];

	// Build AI insight paragraph
	const streakNames = streaks
		.filter((s) => s.id.startsWith("cat-"))
		.map((s) => s.text.split(" ≥")[0])
		.slice(0, 2);
	const streakSentence =
		streakNames.length > 0
			? ` Two active streaks: ${streakNames.join(" and ")}, both ≥7 for ${series.length} meetings running.`
			: "";
	const liftSentence =
		topLift && topLift.lift > 0
			? ` Biggest unlock so far: ${CATEGORY_LABELS[topLift.k]} climbed +${topLift.lift.toFixed(1)} across the tracked meetings.`
			: "";

	const sparkColor = delta >= 0 ? "var(--color-semantic-success)" : "var(--color-warning)";

	return (
		<section className="rounded-lg border border-hairline bg-surface-1 p-6">
			{/* Zone A — score ring + name + delta + sparkline */}
			<div className="grid grid-cols-[auto_1fr_auto] items-center gap-6">
				<ScoreRing value={latest.overallScore} size={96} stroke={6} />
				<div className="min-w-0">
					<div className="text-eyebrow uppercase text-ink-tertiary">Your performance</div>
					<h2 className="display-md mt-0.5 text-ink">{speaker.name}</h2>
					<div className="mt-1.5 flex items-center gap-3 text-body-sm">
						<Delta value={delta} />
						<span className="text-ink-subtle">pts since first meeting</span>
						<span className="text-ink-tertiary">·</span>
						<span className="text-ink-subtle">{series.length} analyses tracked</span>
					</div>
				</div>
				<div className="w-[300px] flex-none">
					<div className="mb-1.5 flex items-baseline justify-between text-meta text-ink-subtle">
						<span>Last {series.length} analyses</span>
						<span className="tabular-nums">
							{earliest.overallScore} →{" "}
							<span className={overallScoreColor(latest.overallScore)}>{latest.overallScore}</span>
						</span>
					</div>
					<Sparkline values={sparkValues} height={40} color={sparkColor} fill={false} />
				</div>
			</div>

			{/* Zone B — AI insight (single paragraph, no pills/boxes) */}
			<div className="mt-5 flex items-start gap-2.5 border-t border-hairline pt-4">
				<span className="mt-0.5 flex-none text-primary">
					<Sparkles className="size-4" />
				</span>
				<p className="text-body-sm text-ink-muted">
					You're at <span className="font-medium text-ink">{latest.overallScore}/100</span> — keep
					the climb going.{liftSentence}
					{streakSentence}
				</p>
			</div>

			{/* Zone C — 6-dimension breakdown */}
			<div className="mt-5 grid grid-cols-6 gap-4 border-t border-hairline pt-4">
				{CATEGORY_KEYS.map((key) => {
					const score = latest.categories[key] ?? 0;
					return (
						<div key={key}>
							<div className="mb-1.5 flex items-baseline justify-between">
								<span className="truncate text-meta text-ink-subtle">{CATEGORY_LABELS[key]}</span>
								<span className={cn("font-mono text-meta tabular-nums", scoreToneClass(score))}>
									{score.toFixed(1)}
								</span>
							</div>
							<ScoreBar value={score} height={3} />
						</div>
					);
				})}
			</div>
		</section>
	);
}

/** Compact tracked-speakers card in the right rail. */
function TrackedSpeakersRail({
	trackedSpeakers,
	meId,
}: {
	trackedSpeakers: SpeakerWithSeries[];
	meId: string | null;
}) {
	return (
		<div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
			<div className="flex items-center justify-between border-b border-hairline px-4 py-2.5">
				<span className="card-title text-ink">Tracked speakers</span>
				<Link
					to="/meeting-perf/speakers"
					className="text-body-sm text-ink-subtle transition-colors hover:text-ink"
				>
					All →
				</Link>
			</div>
			{trackedSpeakers.map((t) => {
				// A tracked speaker may have no/sparse analyses — still list them
				// (the user flagged them); fall back to em-dash where a score/trend
				// would be rather than dropping the row.
				const latest = t.series[t.series.length - 1];
				const earliest = t.series[0];
				const isMe = t.speaker.id === meId;
				return (
					<Link
						key={t.speaker.id}
						to="/meeting-perf/speakers/$id"
						params={{ id: t.speaker.id }}
						className="flex items-center gap-2.5 border-b border-hairline px-4 py-2.5 last:border-0 transition-colors hover:bg-surface-2/40"
					>
						<Avatar
							name={t.speaker.name}
							size="md"
							className={cn(isMe && "ring-2 ring-primary/60 ring-offset-1 ring-offset-surface-1")}
						/>
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-1.5">
								<span className="truncate text-body-sm text-ink">{t.speaker.name}</span>
							</div>
							<div className="text-meta text-ink-subtle">
								{t.series.length} {t.series.length === 1 ? "analysis" : "analyses"}
							</div>
						</div>
						<div className="w-14 flex-none opacity-70">
							{latest && earliest ? (
								<Sparkline
									values={t.series.map((p) => p.overallScore)}
									height={20}
									fill={false}
									color={
										latest.overallScore - earliest.overallScore < 0
											? "var(--color-warning)"
											: "var(--color-success)"
									}
								/>
							) : null}
						</div>
						<div className="flex w-10 flex-col items-end leading-none">
							{latest && earliest ? (
								<>
									<span
										className={cn(
											"text-body-sm font-semibold tabular-nums",
											overallScoreColor(latest.overallScore),
										)}
									>
										{latest.overallScore.toFixed(0)}
									</span>
									<Delta value={latest.overallScore - earliest.overallScore} />
								</>
							) : (
								<span className="text-body-sm font-semibold tabular-nums text-ink-subtle">—</span>
							)}
						</div>
					</Link>
				);
			})}
		</div>
	);
}

/** Team focus areas card in the right rail. */
function TeamFocusAreasCard({ areas }: { areas: { label: string; avg: number }[] }) {
	return (
		<div className="rounded-lg border border-hairline bg-surface-1 p-4">
			<div className="mb-3 card-title text-ink">Team focus areas</div>
			<div className="space-y-2.5">
				{areas.map(({ label, avg }) => (
					<div key={label}>
						<div className="mb-1 flex items-baseline justify-between text-body-sm">
							<span className="text-ink-muted">{label}</span>
							<span className={cn("font-mono text-meta tabular-nums", scoreToneClass(avg))}>
								{avg.toFixed(2)} avg
							</span>
						</div>
						<ScoreBar value={avg} height={4} />
					</div>
				))}
				<p className="pt-1 text-meta text-ink-subtle">
					Lowest-scoring dimensions across all tracked speakers — good candidates for next week.
				</p>
			</div>
		</div>
	);
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function IndexPage() {
	const summary = useQuery({
		queryKey: ["meeting-perf", "summary"],
		queryFn: () => meetingPerfApi.dashboardSummary(),
	});

	const data = summary.data;
	const meId = data?.primarySpeaker?.speaker.id ?? null;

	// Derived KPI sparklines and team focus areas
	const avgScoreSparkline = data ? deriveAvgScoreSparkline(data.trackedSpeakers) : [];
	const teamFocusAreas = data ? computeTeamFocusAreas(data.trackedSpeakers) : [];

	return (
		<>
			{summary.isLoading ? <LoadingBar /> : null}

			<PageHeader
				icon={Presentation}
				title="Overview"
				subtitle="Speaking patterns across your tracked meetings."
				actions={
					<>
						<Link to="/meeting-perf/upload" search={{ source: "fireflies" }}>
							<Button variant="secondary" size="sm">
								<Download className="h-3.5 w-3.5" /> Import
							</Button>
						</Link>
						<Link to="/meeting-perf/upload">
							<Button size="sm">
								<Plus className="h-3.5 w-3.5" /> New meeting
							</Button>
						</Link>
					</>
				}
			/>

			<PageBody className="overflow-y-auto px-6 py-5">
				{summary.error ? (
					<p className="text-body-sm text-destructive">Could not load the dashboard.</p>
				) : null}

				{data ? (
					<div className="mx-auto w-full max-w-5xl space-y-5">
						{/* 1. KPI strip — 4 inline tiles */}
						<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
							{/* Meetings — no sparkline (no time-series source) */}
							<div className="rounded-lg border border-hairline bg-surface-1 p-4">
								<div className="text-eyebrow uppercase text-ink-tertiary">Meetings</div>
								<div className="mt-1 flex items-end justify-between gap-2">
									<span className="display-md tabular-nums text-ink">{data.meetingCount}</span>
								</div>
								<div className="mt-1 text-meta text-ink-subtle">Tracked in this workspace</div>
							</div>

							{/* Analyses — no sparkline */}
							<div className="rounded-lg border border-hairline bg-surface-1 p-4">
								<div className="text-eyebrow uppercase text-ink-tertiary">Analyses</div>
								<div className="mt-1 flex items-end justify-between gap-2">
									<span className="display-md tabular-nums text-ink">{data.analysisCount}</span>
								</div>
								<div className="mt-1 text-meta text-ink-subtle">
									{data.meetingCount > 0
										? `${(data.analysisCount / data.meetingCount).toFixed(1)} per meeting`
										: "Across all meetings"}
								</div>
							</div>

							{/* Avg score — gets the derived sparkline */}
							<div className="rounded-lg border border-hairline bg-surface-1 p-4">
								<div className="text-eyebrow uppercase text-ink-tertiary">Avg score</div>
								<div className="mt-1 flex items-end justify-between gap-2">
									<span
										className={cn(
											"display-md tabular-nums",
											overallScoreColor(data.averageOverallScore),
										)}
									>
										{data.averageOverallScore.toFixed(2)}
									</span>
									{avgScoreSparkline.length >= 2 ? (
										<div className="mb-1 opacity-70">
											<Sparkline
												values={avgScoreSparkline}
												height={22}
												color="var(--color-primary)"
												fill={false}
											/>
										</div>
									) : null}
								</div>
								<div className="mt-1 text-meta text-ink-subtle">Across all speakers</div>
							</div>

							{/* Tracked speakers — no sparkline */}
							<div className="rounded-lg border border-hairline bg-surface-1 p-4">
								<div className="text-eyebrow uppercase text-ink-tertiary">Tracked speakers</div>
								<div className="mt-1 flex items-end justify-between gap-2">
									<span className="display-md tabular-nums text-ink">
										{data.trackedSpeakers.length}
									</span>
								</div>
								<div className="mt-1 text-meta text-ink-subtle">With a trend so far</div>
							</div>
						</div>

						{/* 2. Hero "Your performance" */}
						{data.primarySpeaker ? (
							<HeroSection sw={data.primarySpeaker} />
						) : (
							<section className="rounded-lg border border-hairline bg-surface-1">
								<EmptyState
									icon={Presentation}
									title="Analyze your first meeting"
									body="Paste a transcript or pull one from Fireflies. We'll split it by speaker, score each across six KPIs, and start tracking improvement over time."
									primary={
										<Link to="/meeting-perf/upload">
											<Button>
												<Plus className="h-4 w-4" /> New meeting
											</Button>
										</Link>
									}
								/>
							</section>
						)}

						{/* 3. Two-column body */}
						<div className="grid grid-cols-1 gap-4 items-start lg:grid-cols-[1fr_320px]">
							{/* Left — Recent meetings */}
							<section>
								<div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
									<div className="flex items-center justify-between border-b border-hairline px-4 py-2.5">
										<span className="card-title text-ink">Recent meetings</span>
										<Link
											to="/meeting-perf/meetings"
											className="text-body-sm text-ink-subtle transition-colors hover:text-ink"
										>
											All meetings →
										</Link>
									</div>
									{data.recentMeetings.length === 0 ? (
										<EmptyState
											small
											icon={Calendar}
											title="No meetings yet"
											body="Upload a transcript to get started."
										/>
									) : (
										<>
											<ListHeader
												columns={[
													{ label: "Meeting", width: "1fr" },
													{ label: "Date", width: "90px" },
													{ label: "Speakers", width: "110px" },
													{ label: "Score", width: "70px", align: "right" },
													{ label: "", width: "22px" },
												]}
											/>
											{data.recentMeetings.map((m) => {
												const dateStr = new Date(m.meetingDate).toLocaleDateString(undefined, {
													month: "short",
													day: "numeric",
												});
												return (
													<ListRow
														key={m.id}
														cells={[
															{
																value: <span className="font-medium text-ink">{m.title}</span>,
																width: "1fr",
															},
															{ value: dateStr, muted: true, width: "90px" },
															{
																value: (
																	<div className="flex -space-x-1.5">
																		{m.attendees.slice(0, 4).map((a) => (
																			<Avatar
																				key={a.id}
																				name={a.name}
																				size="md"
																				className="h-5 w-5 text-[8px]"
																			/>
																		))}
																	</div>
																),
																width: "110px",
															},
															{
																value:
																	m.averageScore != null ? (
																		<span
																			className={cn(
																				"font-medium tabular-nums",
																				overallScoreColor(m.averageScore),
																			)}
																		>
																			{m.averageScore.toFixed(0)}
																		</span>
																	) : (
																		<span className="text-meta text-ink-subtle">—</span>
																	),
																width: "70px",
																align: "right",
															},
															{
																value: <ChevronRight className="size-3.5 text-ink-subtle" />,
																width: "22px",
																align: "right",
															},
														]}
														render={(p) => (
															<Link to="/meeting-perf/meetings/$id" params={{ id: m.id }} {...p} />
														)}
													/>
												);
											})}
										</>
									)}
								</div>
							</section>

							{/* Right rail */}
							<div className="space-y-4">
								{data.trackedSpeakers.length > 0 ? (
									<TrackedSpeakersRail trackedSpeakers={data.trackedSpeakers} meId={meId} />
								) : null}
								{teamFocusAreas.length > 0 ? <TeamFocusAreasCard areas={teamFocusAreas} /> : null}
							</div>
						</div>
					</div>
				) : null}
			</PageBody>
		</>
	);
}
